// Generic CRM template editor — defines field-level mappings between an arbitrary
// CRM API response and Stratus's screen-pop expectations.
//
// Output mapping rule shape:
//   {
//     vendor: 'genericwebhook' | 'salesforce' | 'hubspot' | 'zoho' | 'custom',
//     contact_lookup_url: 'https://crm.example.com/contacts?phone={{caller}}',
//     auth: { type: 'bearer'|'basic'|'apikey', value: '...' },
//     mapping: {
//       contactName: '$.results[0].displayName',
//       email:       '$.results[0].emailAddresses[0].address',
//       company:     '$.results[0].companies[0]',
//       popUrl:      'https://crm.example.com/contacts/{{$.results[0].id}}'
//     }
//   }

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { api, getToken } from '../lib/api';

const FIELDS = [
  { key: 'contactName', label: 'Contact name', required: true },
  { key: 'email',       label: 'Email' },
  { key: 'company',     label: 'Company' },
  { key: 'jobTitle',    label: 'Job title' },
  { key: 'popUrl',      label: 'Pop-up URL' },
];

export default function CrmTemplateEditor() {
  const qc = useQueryClient();
  const { data: cur, isLoading } = useQuery<any>({
    queryKey: ['crm-template'],
    queryFn: async () => {
      const r = await fetch('/api/crm-template', { headers: { Authorization: `Bearer ${getToken()}` } });
      return r.ok ? r.json() : null;
    },
  });
  const [edited, setEdited] = useState<any>(null);
  const tmpl = edited ?? cur ?? {
    vendor: 'genericwebhook',
    contact_lookup_url: '',
    auth: { type: 'apikey', value: '' },
    mapping: {},
  };

  const [testInput, setTestInput] = useState('+15555551234');
  const [testResult, setTestResult] = useState<any>(null);

  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/crm-template', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(tmpl),
      });
      if (!r.ok) throw new Error(await r.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-template'] }),
  });

  async function test() {
    setTestResult({ status: 'querying' });
    try {
      const r = await fetch('/api/crm-template/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ caller: testInput, template: tmpl }),
      });
      setTestResult(await r.json());
    } catch (e: any) {
      setTestResult({ error: e.message });
    }
  }

  if (isLoading) return <div>Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">CRM Template Editor</h1>
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> Save</button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="card space-y-3">
          <h2 className="font-semibold">Lookup</h2>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Vendor</label>
            <select className="input" value={tmpl.vendor} onChange={e => setEdited({ ...tmpl, vendor: e.target.value })}>
              {['genericwebhook','salesforce','hubspot','zoho','custom'].map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Contact lookup URL</label>
            <input className="input font-mono text-xs" value={tmpl.contact_lookup_url}
              onChange={e => setEdited({ ...tmpl, contact_lookup_url: e.target.value })} placeholder="https://crm.example.com/contacts?phone={{caller}}" />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Auth type</label>
            <select className="input" value={tmpl.auth?.type} onChange={e => setEdited({ ...tmpl, auth: { ...tmpl.auth, type: e.target.value } })}>
              {['bearer','basic','apikey','none'].map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Auth value</label>
            <input className="input font-mono text-xs" type="password" value={tmpl.auth?.value || ''}
              onChange={e => setEdited({ ...tmpl, auth: { ...tmpl.auth, value: e.target.value } })} />
          </div>
        </div>
        <div className="card space-y-3">
          <h2 className="font-semibold">Field mapping (JSONPath)</h2>
          <p className="text-xs text-textSecondary">Use JSONPath like <code className="font-mono">$.results[0].name</code> or template strings like <code className="font-mono">{'{{$.id}}'}</code>.</p>
          {FIELDS.map(f => (
            <div key={f.key}>
              <label className="block text-sm text-textSecondary mb-1">{f.label}{f.required ? ' *' : ''}</label>
              <input className="input font-mono text-xs" value={tmpl.mapping?.[f.key] || ''}
                onChange={e => setEdited({ ...tmpl, mapping: { ...(tmpl.mapping || {}), [f.key]: e.target.value } })} />
            </div>
          ))}
        </div>
      </div>
      <div className="card space-y-3">
        <h2 className="font-semibold">Test</h2>
        <div className="flex gap-2">
          <input className="input flex-1" placeholder="+15555551234" value={testInput} onChange={e => setTestInput(e.target.value)} />
          <button onClick={test} className="btn-primary">Run</button>
        </div>
        {testResult && (
          <pre className="text-xs font-mono bg-bg p-3 rounded overflow-auto max-h-64">{JSON.stringify(testResult, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}
